/**
 * Ministry Role Service
 * Handles ministry role management operations
 * Microservice: departmentmanagement
 * Domain: departments
 */
import { BaseService } from '../base/BaseService';
export class MinistryRoleService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? MinistryRoleService.DEFAULT_SERVICE_PATH,
            basePath: MinistryRoleService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all ministry roles with optional active status filter
     * @param isActive - Optional filter for active/inactive roles
     * @returns Array of ministry roles
     */
    async getAll(isActive) {
        const response = await this.getAllRaw(isActive);
        return response.data ?? [];
    }
    /**
     * Get all ministry roles with optional active status filter (raw response)
     * @param isActive - Optional filter for active/inactive roles
     * @returns Raw response with array of ministry roles
     */
    getAllRaw(isActive) {
        const params = isActive !== undefined ? { isActive } : {};
        const url = this.buildUrlWithParams('', params);
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    /**
     * Get ministry role by ID
     * @param id - Ministry role ID
     * @returns Ministry role details
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Ministry role with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get ministry role by ID (raw response)
     * @param id - Ministry role ID
     * @returns Raw response with ministry role details
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new ministry role
     * @param data - Ministry role creation data
     * @returns Created ministry role
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create ministry role');
        }
        return response.data;
    }
    /**
     * Create a new ministry role (raw response)
     * @param data - Ministry role creation data
     * @returns Raw response with created ministry role
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing ministry role
     * @param id - Ministry role ID
     * @param data - Ministry role update data
     * @returns Updated ministry role
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update ministry role with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing ministry role (raw response)
     * @param id - Ministry role ID
     * @param data - Ministry role update data
     * @returns Raw response with updated ministry role
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Soft delete a ministry role
     * @param id - Ministry role ID
     * @returns Success status
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Soft delete a ministry role (raw response)
     * @param id - Ministry role ID
     * @returns Raw response with success status
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== CACHE ====================
    /**
     * Invalidate ministry role cache
     * @returns Success status
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate ministry role cache (raw response)
     * @returns Raw response with success status
     */
    invalidateCacheRaw() {
        const url = this.buildUrl('cache/invalidate');
        return this.http.post(url, {});
    }
}
MinistryRoleService.DEFAULT_SERVICE_PATH = 'departments';
MinistryRoleService.BASE_PATH = 'api/ministry-role';
//# sourceMappingURL=MinistryRoleService.js.map